/**
 * Workforce Roster Page
 *
 * Manages active workforce deployment including:
 * - Deployed staff listing with assignments
 * - Location/service area mapping
 * - Availability and status tracking
 * - Role assignments and certifications
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ClipboardList,
  Plus,
  Search,
  Filter,
  MapPin,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  ChevronRight,
  X,
  Edit2,
  Phone,
  Mail,
  Award,
  Briefcase,
  Building2,
  User,
  Download,
  MoreVertical,
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { PageHeader, PageContainer } from '@/components/weavein';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';

// Helper to get auth token from admin dashboard localStorage
const getAuthToken = (): string | null => {
  return localStorage.getItem('auth_token');
};

interface RosterMember {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  user_phone: string | null;
  role_id: number;
  role_name: string;
  role_type: string;
  status: string;
  assignment_type: string;
  service_areas: string[];
  start_date: string;
  end_date: string | null;
  hours_per_week: number;
  supervisor_id: number | null;
  supervisor_name: string | null;
  certifications_count: number;
  active_certifications: number;
  expiring_certifications: number;
  last_activity: string | null;
}

interface RosterStats {
  total_active: number;
  by_role: Record<string, number>;
  by_status: Record<string, number>;
  expiring_certs: number;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string; icon: any }> = {
  active: { label: 'Active', color: 'text-emerald-700', bgColor: 'bg-emerald-100', icon: CheckCircle },
  on_leave: { label: 'On Leave', color: 'text-amber-700', bgColor: 'bg-amber-100', icon: Clock },
  inactive: { label: 'Inactive', color: 'text-ink-700', bgColor: 'bg-paper-2', icon: XCircle },
  terminated: { label: 'Terminated', color: 'text-red-700', bgColor: 'bg-red-100', icon: XCircle },
};

const ASSIGNMENT_TYPES: Record<string, string> = {
  full_time: 'Full-Time',
  part_time: 'Part-Time',
  contract: 'Contract',
  volunteer: 'Volunteer',
};

interface Role {
  id: number;
  role_code: string;
  role_name: string;
}

interface AvailableUser {
  id: number;
  name: string;
  email: string;
}

interface NewRosterForm {
  user_id: string;
  role_id: string;
  assignment_type: string;
  hours_per_week: number;
}

const initialRosterForm: NewRosterForm = {
  user_id: '',
  role_id: '',
  assignment_type: 'full_time',
  hours_per_week: 40,
};

export default function WorkforceRosterPage() {
  const { t } = useAppTranslation();
  const [roster, setRoster] = useState<RosterMember[]>([]);
  const [stats, setStats] = useState<RosterStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [selectedMember, setSelectedMember] = useState<RosterMember | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newRosterEntry, setNewRosterEntry] = useState<NewRosterForm>(initialRosterForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [availableUsers, setAvailableUsers] = useState<AvailableUser[]>([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = getAuthToken();
    if (!token) {
      console.warn('No auth token found for roster page');
      setLoading(false);
      return;
    }

    try {
      const [rosterRes, statsRes] = await Promise.all([
        authFetch(getWeaveinApiUrl('/api/workforce/roster')),
        authFetch(getWeaveinApiUrl('/api/workforce/roster/stats')),
      ]);

      if (rosterRes.ok) {
        const data = await rosterRes.json();
        setRoster(data.items || []);
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching roster:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRolesAndUsers = async () => {
    const token = getAuthToken();
    if (!token) return;

    try {
      const [rolesRes, usersRes] = await Promise.all([
        authFetch(getWeaveinApiUrl('/api/workforce/roles')),
        authFetch(getWeaveinApiUrl('/api/users?limit=200')),
      ]);

      if (rolesRes.ok) {
        const data = await rolesRes.json();
        setRoles(data.items || []);
      }

      if (usersRes.ok) {
        const data = await usersRes.json();
        const existingUserIds = new Set(roster.map(r => r.user_id));
        setAvailableUsers(
          (data.items || data || [])
            .filter((u: any) => !existingUserIds.has(u.id))
            .map((u: any) => ({ id: u.id, name: u.name, email: u.email }))
        );
      }
    } catch (error) {
      console.error('Error fetching roles/users:', error);
    }
  };

  const handleOpenAddModal = () => {
    fetchRolesAndUsers();
    setShowAddModal(true);
  };

  const handleAddToRoster = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);

    try {
      const response = await authFetch(getWeaveinApiUrl('/api/workforce/roster'), {
        method: 'POST',
        jsonBody: {
          user_id: parseInt(newRosterEntry.user_id),
          role_id: parseInt(newRosterEntry.role_id),
          assignment_type: newRosterEntry.assignment_type,
          hours_per_week: newRosterEntry.hours_per_week,
          status: 'active',
        },
      });

      if (response.ok) {
        setShowAddModal(false);
        setNewRosterEntry(initialRosterForm);
        fetchData();
      } else {
        const errorData = await response.json();
        // Handle Pydantic validation errors (422) which return detail as an array
        let errorMessage = 'Failed to add to roster';
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join('; ');
        } else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (errorData.detail) {
          errorMessage = JSON.stringify(errorData.detail);
        }
        setSubmitError(errorMessage);
      }
    } catch (error) {
      console.error('Error adding to roster:', error);
      setSubmitError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async (memberId: number, newStatus: string) => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const res = await authFetch(getWeaveinApiUrl(`/api/workforce/roster/${memberId}/status`), {
        method: 'PATCH',
        jsonBody: { status: newStatus },
      });

      if (res.ok) {
        fetchData();
        if (selectedMember?.id === memberId) {
          setSelectedMember({ ...selectedMember, status: newStatus });
        }
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const filteredRoster = roster.filter(m => {
    const matchesSearch =
      m.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.user_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.role_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.service_areas?.some(a => a.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = statusFilter === 'all' || m.status === statusFilter;
    const matchesRole = roleFilter === 'all' || m.role_type === roleFilter;

    return matchesSearch && matchesStatus && matchesRole;
  });

  const uniqueRoles = [...new Set(roster.map(m => m.role_type))];

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Workforce · Roster"
        title={t('workforce.roster.title', 'Workforce Roster')}
        description={t('workforce.roster.description', 'Track active workforce members and their assignments')}
        actions={
          <>
            <button className="inline-flex items-center gap-1.5 rounded-md border border-v2border bg-surface px-2.5 py-1.5 text-xs font-medium text-ink hover:bg-paper-2">
              <Download className="w-4 h-4" />
              <span>{t('workforce.roster.export', 'Export')}</span>
            </button>
            <button
              onClick={handleOpenAddModal}
              className="inline-flex items-center gap-1.5 rounded-md bg-v2accent px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
            >
              <Plus className="w-4 h-4" />
              <span>{t('workforce.roster.addToRoster', 'Add to Roster')}</span>
            </button>
          </>
        }
      />
      <PageContainer>
      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-surface rounded-xl p-4 border border-v2border">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <ClipboardList className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-ink-500">{t('workforce.roster.stats.totalActive', 'Total Active')}</p>
                <p className="font-display text-2xl font-semibold text-ink">{stats?.total_active || roster.filter(r => r.status === 'active').length}</p>
              </div>
            </div>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-v2border">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Briefcase className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-ink-500">{t('workforce.roster.stats.chws', 'CHWs')}</p>
                <p className="font-display text-2xl font-semibold text-ink">{stats?.by_role?.chw || roster.filter(r => r.role_type === 'chw').length}</p>
              </div>
            </div>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-v2border">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <User className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-ink-500">{t('workforce.roster.stats.navigators', 'Navigators')}</p>
                <p className="font-display text-2xl font-semibold text-ink">{stats?.by_role?.navigator || roster.filter(r => r.role_type === 'navigator').length}</p>
              </div>
            </div>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-v2border">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Award className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-ink-500">{t('workforce.roster.stats.expiringCerts', 'Expiring Certs')}</p>
                <p className="font-display text-2xl font-semibold text-amber-600">{stats?.expiring_certs || roster.reduce((sum, r) => sum + (r.expiring_certifications || 0), 0)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
            <input
              type="text"
              placeholder={t('workforce.roster.searchPlaceholder', 'Search by name, email, role, or location...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <option value="all">{t('workforce.roster.allStatuses', 'All Statuses')}</option>
            {Object.entries(STATUS_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>{config.label}</option>
            ))}
          </select>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-4 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <option value="all">{t('workforce.roster.allRoles', 'All Roles')}</option>
            {uniqueRoles.map((role) => (
              <option key={role} value={role}>{role.toUpperCase()}</option>
            ))}
          </select>
        </div>

        {/* Roster Table */}
        <div className="bg-surface rounded-xl border border-v2border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-paper border-b border-v2border">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-medium text-ink-500 uppercase">{t('workforce.roster.table.teamMember', 'Team Member')}</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-ink-500 uppercase">{t('workforce.roster.table.role', 'Role')}</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-ink-500 uppercase">{t('workforce.roster.table.status', 'Status')}</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-ink-500 uppercase">{t('workforce.roster.table.serviceAreas', 'Service Areas')}</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-ink-500 uppercase">{t('workforce.roster.table.hours', 'Hours')}</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-ink-500 uppercase">{t('workforce.roster.table.certs', 'Certs')}</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-ink-500 uppercase">{t('workforce.roster.table.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-ink-500">
                      {t('workforce.roster.loading', 'Loading roster...')}
                    </td>
                  </tr>
                ) : filteredRoster.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <ClipboardList className="w-10 h-10 text-ink-400 mx-auto mb-2" />
                      <p className="text-ink-500">{t('workforce.roster.noMembers', 'No roster members found')}</p>
                      <button
                        onClick={() => setShowAddModal(true)}
                        className="text-emerald-600 hover:text-emerald-700 font-medium mt-2"
                      >
                        {t('workforce.roster.addFirstMember', 'Add your first team member')}
                      </button>
                    </td>
                  </tr>
                ) : (
                  filteredRoster.map((member) => {
                    const statusConfig = STATUS_CONFIG[member.status] || STATUS_CONFIG.active;
                    const StatusIcon = statusConfig.icon;

                    return (
                      <tr
                        key={member.id}
                        className="hover:bg-paper cursor-pointer"
                        onClick={() => setSelectedMember(member)}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                              <span className="text-emerald-600 font-medium">
                                {member.user_name.split(' ').map(n => n[0]).join('')}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-ink">{member.user_name}</p>
                              <p className="text-sm text-ink-500">{member.user_email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="font-medium text-ink">{member.role_name}</p>
                          <p className="text-sm text-ink-500">{ASSIGNMENT_TYPES[member.assignment_type] || member.assignment_type}</p>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.bgColor} ${statusConfig.color}`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusConfig.label}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {member.service_areas && member.service_areas.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {member.service_areas.slice(0, 2).map((area, i) => (
                                <span
                                  key={i}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-paper-2 text-ink-500"
                                >
                                  <MapPin className="w-3 h-3 mr-1" />
                                  {area}
                                </span>
                              ))}
                              {member.service_areas.length > 2 && (
                                <span className="text-xs text-ink-500">+{member.service_areas.length - 2}</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-ink-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-ink">{member.hours_per_week}h/wk</span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-1">
                            <span className="text-sm font-medium text-ink">{member.active_certifications}</span>
                            {member.expiring_certifications > 0 && (
                              <span className="flex items-center text-amber-600">
                                <AlertCircle className="w-4 h-4 ml-1" />
                                <span className="text-xs ml-0.5">{member.expiring_certifications}</span>
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedMember(member);
                            }}
                            className="p-1.5 text-ink-500 hover:bg-paper-2 rounded"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Member Detail Slide-out */}
        {selectedMember && (
          <div className="fixed inset-0 z-50 overflow-hidden">
            <div className="absolute inset-0 bg-black/50" onClick={() => setSelectedMember(null)} />
            <div className="absolute right-0 top-0 bottom-0 w-full max-w-lg bg-surface shadow-xl">
              <div className="h-full flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-v2border flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-ink">{t('workforce.roster.detail.title', 'Team Member Details')}</h2>
                  <div className="flex items-center space-x-2">
                    <button className="p-2 hover:bg-paper-2 rounded">
                      <Edit2 className="w-5 h-5 text-ink-500" />
                    </button>
                    <button
                      onClick={() => setSelectedMember(null)}
                      className="p-2 hover:bg-paper-2 rounded"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                  {/* Profile Header */}
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                      <span className="text-emerald-600 font-bold text-xl">
                        {selectedMember.user_name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-ink">{selectedMember.user_name}</h3>
                      <p className="text-ink-500">{selectedMember.role_name}</p>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${STATUS_CONFIG[selectedMember.status]?.bgColor} ${STATUS_CONFIG[selectedMember.status]?.color}`}>
                        {STATUS_CONFIG[selectedMember.status]?.label}
                      </span>
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="bg-paper rounded-xl p-4 space-y-3">
                    <h4 className="font-medium text-ink mb-3">{t('workforce.roster.detail.contactInfo', 'Contact Information')}</h4>
                    <div className="flex items-center space-x-3">
                      <Mail className="w-4 h-4 text-ink-400" />
                      <span className="text-ink-500">{selectedMember.user_email}</span>
                    </div>
                    {selectedMember.user_phone && (
                      <div className="flex items-center space-x-3">
                        <Phone className="w-4 h-4 text-ink-400" />
                        <span className="text-ink-500">{selectedMember.user_phone}</span>
                      </div>
                    )}
                  </div>

                  {/* Assignment Info */}
                  <div className="bg-paper rounded-xl p-4 space-y-3">
                    <h4 className="font-medium text-ink mb-3">{t('workforce.roster.detail.assignmentDetails', 'Assignment Details')}</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-ink-500">{t('workforce.roster.detail.type', 'Type')}</p>
                        <p className="font-medium text-ink">
                          {ASSIGNMENT_TYPES[selectedMember.assignment_type] || selectedMember.assignment_type}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-ink-500">{t('workforce.roster.detail.hoursWeek', 'Hours/Week')}</p>
                        <p className="font-medium text-ink">{selectedMember.hours_per_week}h</p>
                      </div>
                      <div>
                        <p className="text-sm text-ink-500">{t('workforce.roster.detail.startDate', 'Start Date')}</p>
                        <p className="font-medium text-ink">
                          {new Date(selectedMember.start_date).toLocaleDateString()}
                        </p>
                      </div>
                      {selectedMember.supervisor_name && (
                        <div>
                          <p className="text-sm text-ink-500">{t('workforce.roster.detail.supervisor', 'Supervisor')}</p>
                          <p className="font-medium text-ink">{selectedMember.supervisor_name}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Service Areas */}
                  <div>
                    <h4 className="font-medium text-ink mb-3">{t('workforce.roster.detail.serviceAreas', 'Service Areas')}</h4>
                    {selectedMember.service_areas && selectedMember.service_areas.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {selectedMember.service_areas.map((area, i) => (
                          <span
                            key={i}
                            className="inline-flex items-center px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 text-sm"
                          >
                            <MapPin className="w-4 h-4 mr-1" />
                            {area}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-ink-500">{t('workforce.roster.detail.noServiceAreas', 'No service areas assigned')}</p>
                    )}
                  </div>

                  {/* Certifications */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-ink">{t('workforce.roster.detail.certifications', 'Certifications')}</h4>
                      <Link
                        to={`/workforce/certifications?user=${selectedMember.user_id}`}
                        className="text-sm text-emerald-600 hover:text-emerald-700"
                      >
                        {t('workforce.roster.detail.viewAll', 'View All')}
                      </Link>
                    </div>
                    <div className="grid grid-cols-3 gap-4 bg-paper rounded-xl p-4">
                      <div className="text-center">
                        <p className="font-display text-2xl font-semibold text-ink">{selectedMember.certifications_count}</p>
                        <p className="text-xs text-ink-500">{t('workforce.roster.detail.total', 'Total')}</p>
                      </div>
                      <div className="text-center">
                        <p className="font-display text-2xl font-semibold text-emerald-600">{selectedMember.active_certifications}</p>
                        <p className="text-xs text-ink-500">{t('workforce.roster.detail.active', 'Active')}</p>
                      </div>
                      <div className="text-center">
                        <p className="font-display text-2xl font-semibold text-amber-600">{selectedMember.expiring_certifications}</p>
                        <p className="text-xs text-ink-500">{t('workforce.roster.detail.expiring', 'Expiring')}</p>
                      </div>
                    </div>
                  </div>

                  {/* Status Actions */}
                  <div>
                    <h4 className="font-medium text-ink mb-3">{t('workforce.roster.detail.changeStatus', 'Change Status')}</h4>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                        <button
                          key={key}
                          onClick={() => handleUpdateStatus(selectedMember.id, key)}
                          disabled={selectedMember.status === key}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            selectedMember.status === key
                              ? `${config.bgColor} ${config.color} cursor-not-allowed`
                              : 'bg-paper-2 text-ink-500 hover:bg-paper-2'
                          }`}
                        >
                          {config.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-v2border flex items-center justify-between">
                  <button className="px-4 py-2 text-ink-700 hover:bg-paper-2 rounded-lg">
                    {t('workforce.roster.detail.viewFullProfile', 'View Full Profile')}
                  </button>
                  <button className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">
                    {t('workforce.roster.detail.editAssignment', 'Edit Assignment')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add to Roster Modal */}
        {showAddModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <div className="fixed inset-0 bg-black/50" onClick={() => setShowAddModal(false)} />
              <div className="relative bg-surface rounded-xl shadow-xl w-full max-w-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-ink">{t('workforce.roster.addModal.title', 'Add to Roster')}</h2>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="p-2 hover:bg-paper-2 rounded"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {submitError && (
                  <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                    {submitError}
                  </div>
                )}

                <form onSubmit={handleAddToRoster} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">{t('workforce.roster.addModal.user', 'User')} *</label>
                    <select
                      required
                      value={newRosterEntry.user_id}
                      onChange={(e) => setNewRosterEntry({ ...newRosterEntry, user_id: e.target.value })}
                      className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      <option value="">{t('workforce.roster.addModal.selectUser', 'Select a user...')}</option>
                      {availableUsers.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name} ({user.email})
                        </option>
                      ))}
                    </select>
                    {availableUsers.length === 0 && (
                      <p className="text-sm text-ink-500 mt-1">
                        {t('workforce.roster.addModal.allUsersOnRoster', 'All users are already on the roster.')}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">{t('workforce.roster.addModal.role', 'Role')} *</label>
                    <select
                      required
                      value={newRosterEntry.role_id}
                      onChange={(e) => setNewRosterEntry({ ...newRosterEntry, role_id: e.target.value })}
                      className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      <option value="">{t('workforce.roster.addModal.selectRole', 'Select a role...')}</option>
                      {roles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.role_name} ({role.role_code})
                        </option>
                      ))}
                    </select>
                    {roles.length === 0 && (
                      <p className="text-sm text-ink-500 mt-1">
                        {t('workforce.roster.addModal.noRoles', 'No roles defined. Create roles first.')}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-ink-700 mb-1">{t('workforce.roster.addModal.assignmentType', 'Assignment Type')}</label>
                      <select
                        value={newRosterEntry.assignment_type}
                        onChange={(e) => setNewRosterEntry({ ...newRosterEntry, assignment_type: e.target.value })}
                        className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      >
                        <option value="full_time">{t('workforce.roster.addModal.fullTime', 'Full-Time')}</option>
                        <option value="part_time">{t('workforce.roster.addModal.partTime', 'Part-Time')}</option>
                        <option value="contract">{t('workforce.roster.addModal.contract', 'Contract')}</option>
                        <option value="volunteer">{t('workforce.roster.addModal.volunteer', 'Volunteer')}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-ink-700 mb-1">{t('workforce.roster.addModal.hoursWeek', 'Hours/Week')}</label>
                      <input
                        type="number"
                        min={1}
                        max={80}
                        value={newRosterEntry.hours_per_week}
                        onChange={(e) => setNewRosterEntry({ ...newRosterEntry, hours_per_week: parseInt(e.target.value) || 40 })}
                        className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowAddModal(false)}
                      className="flex-1 px-4 py-2 border border-v2border rounded-lg hover:bg-paper font-medium"
                    >
                      {t('workforce.roster.addModal.cancel', 'Cancel')}
                    </button>
                    <button
                      type="submit"
                      disabled={submitting || availableUsers.length === 0 || roles.length === 0}
                      className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium disabled:opacity-50"
                    >
                      {submitting ? t('workforce.roster.addModal.adding', 'Adding...') : t('workforce.roster.addModal.addToRoster', 'Add to Roster')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
      </PageContainer>
    </div>
  );
}
